import * as React from 'react'
import { cn } from '@/lib/utils'
import { Kbd } from '@/components/ui/kbd'

type ShortcutHintValue = string | Array<string> | null | undefined

export interface ShortcutHintProps {
  value?: ShortcutHintValue
  className?: string
}

/**
 * Normalizes shortcut value into an array of key strings.
 * Handles both string ("Cmd+K") and array (["Cmd", "K"]) formats.
 */
const normalizeShortcutValue = (
  value: ShortcutHintValue,
): Array<string> | null => {
  if (!value) {
    return null
  }

  if (Array.isArray(value)) {
    const filtered = value.map((key) => key.trim()).filter(Boolean)
    return filtered.length ? filtered : null
  }

  const segments = value
    .split('+')
    .map((segment) => segment.trim())
    .filter(Boolean)

  return segments.length ? segments : null
}

/**
 * Displays keyboard shortcut hints in a styled format with individual key badges.
 * Supports both string format ("Cmd+K") and array format (["Cmd", "K"]).
 *
 * @param value - Shortcut keys as string with "+" separator or array of strings
 * @param className - Additional CSS classes for the container
 *
 * @example
 * ```tsx
 * <ShortcutHint value="Cmd+K" />
 * <ShortcutHint value={["Ctrl", "Shift", "P"]} />
 * ```
 */
export function ShortcutHint({ value, className }: ShortcutHintProps) {
  const normalized = React.useMemo(() => normalizeShortcutValue(value), [value])

  if (!normalized || normalized.length === 0) {
    return null
  }

  return (
    <div className={cn('ml-2 inline-flex items-center gap-1', className)}>
      {normalized.map((key, index) => (
        <React.Fragment key={`${key}-${index}`}>
          <Kbd>{key}</Kbd>
          {index < normalized.length - 1 && (
            <span className="text-[10px] text-muted-foreground">+</span>
          )}
        </React.Fragment>
      ))}
    </div>
  )
}
